// src/components/ChatWidget.js - GROQ AI INTEGRATED CHATBOT

import React, { useState, useEffect, useRef } from "react";
import { 
  X, 
  Send, 
  Sparkles, 
  Bot, 
  User, 
  UserCheck,
  Layers,
  Zap, 
  Mail,
  RotateCcw
} from "lucide-react";
import Logo from "../assets/quivix.png";

const GROQ_MODEL = "llama-3.3-70b-versatile";

const SYSTEM_PROMPT = `You are Quivix AI, the official intelligent assistant for QuivixDigital.
Company Knowledge Base:
- QuivixDigital is a high-performance software engineering studio & digital products incubator.
- Founder & CEO: Salman Khan.
- Metrics & Infrastructure: 60k-100k active users, 18 dedicated global servers, 20-30ms network latency, 99.99% platform uptime.
- Dual Global Headquarters: New Mexico, USA and Jeddah, KSA.
- Real Platform Volume: $3,400 USD.
- Flagship Products: Ziro Platform (social collector & real-time telemetry engine), Chafadi Suite (enterprise e-commerce & logistics system), Quivix Core AI (neural pipeline processing).
- Key Clients & Review Partners: Chafadi Platform (E-Commerce), Nzomi Inc. (Networking & Cloud Systems), MTG Infomatic (Apps, Games & Utility Suite), Agentic AI Labs (Autonomous AI Systems).
- Contact Support: support@quivixdigital.com.

STRICT INSTRUCTION: Always keep your responses concise, clear, and professional. Every single response MUST be between 2 to 5 lines maximum. Never write more than 5 lines.`;

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const chatEndRef = useRef(null);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([
        {
          id: 1,
          sender: "bot",
          text: "Welcome to QuivixDigital! I'm Quivix AI. How can I assist you with our products, infrastructure, or ecosystem today?"
        }
      ]);
    }
  }, [isOpen, messages.length]);

  useEffect(scrollToBottom, [messages, isTyping]);

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  const handleResetChat = () => {
    setMessages([
      {
        id: Date.now(),
        sender: "bot",
        text: "Conversation reset. How can Quivix AI help you?"
      }
    ]);
  };

  const generateFallbackResponse = (query) => {
    const q = query.toLowerCase();
    if (q.includes("who") && (q.includes("ceo") || q.includes("founder") || q.includes("salman"))) {
      return "QuivixDigital was founded by CEO Salman Khan with an execution-first engineering mindset. Under his leadership, the company powers 18 dedicated servers with 20-30ms latency for over 60k-100k active users globally.";
    }
    if (q.includes("latency") || q.includes("server") || q.includes("uptime") || q.includes("specs")) {
      return "QuivixDigital runs on 18 dedicated servers with sub 20-30ms network response times and a 99.99% SLA uptime. Our dual global HQs are located in New Mexico, USA and Jeddah, KSA.";
    }
    if (q.includes("app") || q.includes("product") || q.includes("ziro") || q.includes("chafadi")) {
      return "Our core product suite includes Ziro Engine (real-time telemetry collector), Chafadi Suite (enterprise e-commerce & logistics), and Quivix Core AI (distributed neural processing). Explore full specs on our Apps page!";
    }
    if (q.includes("email") || q.includes("contact") || q.includes("support")) {
      return "You can reach our team directly at support@quivixdigital.com for enterprise inquiries, investor requests, and developer support with an average response SLA within 4 hours.";
    }
    return "QuivixDigital builds high-performance software, modern consumer engines, and AI platforms. Operating across 18 dedicated servers with 20-30ms latency, we serve over 60k+ active users worldwide!";
  };

  const handleSendMessage = async (e, customText = null) => {
    if (e) e.preventDefault();
    const textToSend = customText || inputValue;
    if (!textToSend.trim() || isTyping) return;

    const userMsg = {
      id: Date.now(),
      sender: "user",
      text: textToSend
    };

    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    if (!customText) setInputValue("");
    setIsTyping(true);

    const apiKey = process.env.REACT_APP_GROQ_API_KEY;

    // Check if Groq Key exists
    if (apiKey) {
      try {
        const apiMessages = [
          { role: "system", content: SYSTEM_PROMPT },
          ...newMessages.map((m) => ({
            role: m.sender === "user" ? "user" : "assistant",
            content: m.text
          }))
        ];

        const res = await fetch("https://api.groq.com/openai/v1/chat/completions", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${apiKey}`
          },
          body: JSON.stringify({
            model: GROQ_MODEL,
            messages: apiMessages,
            temperature: 0.5,
            max_tokens: 250
          })
        });

        if (!res.ok) {
          throw new Error(`Groq API Status: ${res.status}`);
        }

        const data = await res.json();
        const botReply = data.choices?.[0]?.message?.content || generateFallbackResponse(textToSend);

        setMessages((prev) => [
          ...prev,
          {
            id: Date.now() + 1,
            sender: "bot",
            text: botReply.trim()
          }
        ]);
      } catch (err) {
        console.warn("Groq AI API call fallback:", err);
        setTimeout(() => {
          setMessages((prev) => [
            ...prev,
            {
              id: Date.now() + 1,
              sender: "bot",
              text: generateFallbackResponse(textToSend)
            }
          ]);
        }, 600);
      } finally {
        setIsTyping(false);
      }
    } else {
      // Smart instant fallback response
      setTimeout(() => {
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now() + 1,
            sender: "bot",
            text: generateFallbackResponse(textToSend)
          }
        ]);
        setIsTyping(false);
      }, 700);
    }
  };

const quickPromptCards = [
  {
    text: "Who is the CEO of QuivixDigital?",
    icon: <UserCheck className="w-4 h-4 text-blue-400" />,
    bg: "bg-blue-500/10 border-blue-500/20 hover:border-blue-400 text-blue-200"
  },
  {
    text: "What are your server specs & latency?",
    icon: <Zap className="w-4 h-4 text-amber-400" />,
    bg: "bg-amber-500/10 border-amber-500/20 hover:border-amber-400 text-amber-200"
  },
  {
    text: "Tell me about your core products",
    icon: <Layers className="w-4 h-4 text-purple-400" />,
    bg: "bg-purple-500/10 border-purple-500/20 hover:border-purple-400 text-purple-200"
  },
  {
    text: "How do I contact support?",
    icon: <Mail className="w-4 h-4 text-emerald-400" />,
    bg: "bg-emerald-500/10 border-emerald-500/20 hover:border-emerald-400 text-emerald-200"
  }
];

  return (
    <>
      {/* Floating Glass Chat Modal */}
      {isOpen && (
        <div className="fixed bottom-24 right-4 sm:right-8 z-50 w-[92vw] sm:w-[420px] h-[560px] rounded-3xl apple-glass border border-white/15 shadow-[0_20px_60px_rgba(0,0,0,0.9)] flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-6 duration-300 font-sans">
          
          {/* Header */}
          <div className="p-4 bg-black/70 border-b border-white/10 flex items-center justify-between backdrop-blur-xl">
            <div className="flex items-center gap-3">
              <div className="relative w-9 h-9 rounded-xl bg-neutral-900 border border-white/20 p-1 flex items-center justify-center shrink-0 shadow-md">
                <img src={Logo} alt="Quivix AI" className="w-full h-full object-contain" />
                <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-black animate-pulse" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-white font-heading">Quivix AI Assistant</h3>
                <p className="text-[10px] text-[#86868b] flex items-center gap-1">
                  <Zap className="w-3 h-3 text-amber-400" /> High-Throughput Engine • Online
                </p>
              </div>
            </div>

            <div className="flex items-center gap-1">
              <button
                onClick={handleResetChat}
                className="p-1.5 text-[#86868b] hover:text-white hover:bg-white/10 rounded-full transition-colors"
                title="Reset Conversation"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
              <button
                onClick={toggleChat}
                className="p-1.5 text-[#86868b] hover:text-white hover:bg-white/10 rounded-full transition-colors"
                aria-label="Close Chat"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Messages Body */}
          <div className="flex-1 p-4 overflow-y-auto space-y-3.5 bg-gradient-to-b from-black/40 via-black/70 to-black/90">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex gap-2.5 ${msg.sender === "user" ? "justify-end" : "justify-start"}`}
              >
                {msg.sender === "bot" && (
                  <div className="w-7 h-7 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center shrink-0 mt-0.5 text-blue-400">
                    <Bot className="w-4 h-4" />
                  </div>
                )}

                <div
                  className={`max-w-[82%] px-4 py-2.5 rounded-2xl text-xs leading-relaxed ${
                    msg.sender === "user"
                      ? "bg-blue-600 text-white rounded-br-none shadow-md"
                      : "bg-white/[0.06] text-[#e1e1e6] border border-white/10 rounded-bl-none shadow-md"
                  }`}
                >
                  {msg.text}
                </div>

                {msg.sender === "user" && (
                  <div className="w-7 h-7 rounded-full bg-white/10 border border-white/20 flex items-center justify-center shrink-0 mt-0.5 text-white">
                    <User className="w-4 h-4" />
                  </div>
                )}
              </div>
            ))}

            {/* Centered Designed Prompt Cards */}
            {messages.length <= 1 && (
              <div className="my-4 p-4 rounded-2xl bg-white/[0.03] border border-white/10 text-center shadow-lg">
                <Sparkles className="w-5 h-5 text-blue-400 mx-auto mb-1.5 animate-pulse" />
                <h4 className="text-xs font-bold text-white font-heading">Popular Inquiry Topics</h4>
                <p className="text-[10px] text-[#86868b] mt-0.5 mb-3.5">Tap a question below to instantly query Quivix AI</p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-left">
                  {quickPromptCards.map((card, idx) => (
                    <button
                      key={idx}
                      onClick={(e) => handleSendMessage(e, card.text)}
                      className={`p-2.5 rounded-xl border text-xs flex items-center gap-2.5 transition-all active:scale-95 shadow-sm hover:shadow-md ${card.bg}`}
                    >
                      <div className="shrink-0 p-1.5 rounded-lg bg-black/40 border border-white/10">
                        {card.icon}
                      </div>
                      <span className="text-[11px] font-medium leading-tight">{card.text}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {isTyping && (
              <div className="flex gap-2.5 justify-start items-center text-xs text-[#86868b] italic">
                <div className="w-7 h-7 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center shrink-0 text-blue-400">
                  <Bot className="w-4 h-4" />
                </div>
                <div className="bg-white/[0.06] px-4 py-2.5 rounded-2xl border border-white/10 rounded-bl-none flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-bounce" />
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-bounce delay-150" />
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-bounce delay-300" />
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Input Footer */}
          <form onSubmit={handleSendMessage} className="p-3 bg-black border-t border-white/10 flex items-center gap-2">
            <input
              type="text"
              placeholder="Ask Quivix AI a question..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              className="flex-1 bg-white/[0.05] border border-white/10 rounded-full px-4 py-2.5 text-xs text-white placeholder-[#86868b] focus:outline-none focus:border-blue-500 transition-colors"
            />
            <button
              type="submit"
              disabled={!inputValue.trim() || isTyping}
              className="p-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white rounded-full transition-all active:scale-95 shadow-md shrink-0"
              aria-label="Send message"
            >
              <Send className="w-3.5 h-3.5" />
            </button>
          </form>

        </div>
      )}

      {/* Floating Trigger Button */}
      <button
        onClick={toggleChat}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-neutral-900 border border-white/20 text-white shadow-[0_0_35px_rgba(41,151,255,0.45)] flex items-center justify-center hover:scale-110 active:scale-95 transition-all duration-300 group"
        aria-label={isOpen ? "Close Chatbot" : "Open Chatbot"}
      >
        <div className="relative">
          {isOpen ? (
            <X className="w-6 h-6 text-white" />
          ) : (
            <Sparkles className="w-6 h-6 text-blue-400 group-hover:text-white transition-colors animate-pulse" />
          )}
          <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-emerald-400 border-2 border-black animate-ping" />
          <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-emerald-400 border-2 border-black" />
        </div>
      </button>
    </>
  );
};

export default ChatWidget;
